import React, { useEffect, useState } from 'react';
import { ArrowRight, Github, Twitter, Linkedin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { trackEvent, Events } from '../lib/fpixel';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) return;
    
    setIsSubmitting(true);
    setSubmitStatus('idle');
    
    try {
      const webhookData = {
        email: email,
        form_type: 'newsletter',
        page_source: 'footer',
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        referrer: document.referrer || 'direct'
      };

      // Track newsletter subscription with Facebook Pixel
      trackEvent(Events.LEAD, {
        content_name: 'Newsletter Subscription',
        content_category: 'newsletter',
        value: 0,
        currency: 'USD',
        status: 'subscribed'
      });

      const response = await fetch('https://hook.us1.make.com/kufviv689kkio8ys9rpipb0g4qyfquro', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookData)
      });

      if (response.ok) {
        setEmail('');
        setSubmitStatus('success');
      } else {
        throw new Error('Subscription failed');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    // Wati WhatsApp chat widget script
    const url = 'https://wati-integration-prod-service.clare.ai/v2/watiWidget.js?29767';
    const s = document.createElement('script');
    s.type = 'text/javascript';
    s.async = true;
    s.src = url;
    const options = {
      "enabled": true,
      "chatButtonSetting": {
        "backgroundColor": "#13171b",
        "ctaText": "Chat with us",
        "borderRadius": "25",
        "marginLeft": "0",
        "marginRight": "20",
        "marginBottom": "20",
        "ctaIconWATI": false,
        "position": "right"
      },
      "brandSetting": {
        "brandName": "1to10x",
        "brandSubTitle": "undefined",
        "brandImg": "https://1to10x.ai/wp-content/uploads/2025/03/FAVICON.png",
        "welcomeText": "Hi there!\nHow can I help you?",
        "messageText": "Hello, %0A I have a question about {{page_link}}",
        "backgroundColor": "#13171b",
        "ctaText": "Chat with us",
        "borderRadius": "25",
        "autoShow": false,
        "phoneNumber": "17602922122"
      }
    };
    s.onload = function() {
      // @ts-ignore - CreateWhatsappChatWidget is loaded from external script
      CreateWhatsappChatWidget(options);
    };
    const x = document.getElementsByTagName('script')[0];
    x.parentNode.insertBefore(s, x);

    // Cleanup function to remove script if component unmounts
    return () => {
      const existingScript = document.querySelector(`script[src="${url}"]`);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <footer style={{ backgroundColor: '#F1EFED' }} className="py-12 sm:py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12 mb-12 sm:mb-16">
          {/* Newsletter Section */}
          <div className="lg:col-span-1">
            <h3 className="text-xl sm:text-2xl font-medium text-gray-900 mb-4 sm:mb-6">
              Subscribe to newsletter
            </h3>
            <form onSubmit={handleNewsletterSubmit} className="space-y-4">
              <div className="flex flex-col sm:flex-row">
                <input
                  type="email"
                  name="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isSubmitting}
                  className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 bg-white border border-gray-300 rounded-lg sm:rounded-l-lg sm:rounded-r-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700 disabled:opacity-50 mb-2 sm:mb-0"
                />
                <button 
                  type="submit"
                  disabled={isSubmitting || !email}
                  className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-900 text-white rounded-lg sm:rounded-l-none sm:rounded-r-lg hover:bg-gray-800 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <ArrowRight className="w-5 h-5" />
                  )}
                </button>
              </div>

              {/* Success Message */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                      <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-green-800 font-semibold text-sm">Welcome to our community!</p>
                      <p className="text-green-600 text-xs">You'll receive exclusive insights and updates from our team.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                      <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-red-800 font-semibold text-sm">Subscription failed</p>
                      <p className="text-red-600 text-xs">Please try again.</p>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* Navigation Links */}
          <div className="lg:col-span-1">
            <nav className="space-y-4">
              {/* <a href="#" className="block text-gray-700 hover:text-blue-600 hover:translate-x-2 transition-all duration-300 ease-out hover:font-medium">Services</a> */}
              <Link to="/about" className="block text-gray-700 hover:text-blue-600 hover:translate-x-2 transition-all duration-300 ease-out hover:font-medium">About</Link>
              <Link to="/career" className="block text-gray-700 hover:text-blue-600 hover:translate-x-2 transition-all duration-300 ease-out hover:font-medium">Career</Link>
              <a href="https://academy.10xdataanalyst.com/" target="_blank" rel="noopener noreferrer" className="block text-gray-700 hover:text-blue-600 hover:translate-x-2 transition-all duration-300 ease-out hover:font-medium">Pricing</a>
              <Link to="/contact" className="block text-gray-700 hover:text-blue-600 hover:translate-x-2 transition-all duration-300 ease-out hover:font-medium">Contact</Link>
              {/* <a href="#" className="block text-gray-700 hover:text-blue-600 hover:translate-x-2 transition-all duration-300 ease-out hover:font-medium">Support</a> */}
              {/* <a href="#" className="block text-gray-700 hover:text-blue-600 hover:translate-x-2 transition-all duration-300 ease-out hover:font-medium">FAQ</a> */}
            </nav>
          </div>

          {/* Social Links */}
          <div className="lg:col-span-1 md:col-span-2 lg:md:col-span-1 flex flex-col">
            <div className="flex space-x-3 sm:space-x-4 mb-6 sm:mb-8">
              <a href="#" className="p-2 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors duration-200">
                <Github className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
              </a>
              <a href="#" className="p-2 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors duration-200">
                <Twitter className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
              </a>
              <a href="#" className="p-2 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors duration-200">
                <Linkedin className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
              </a>
            </div>
            <div className="mt-auto space-y-3">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 3.26a2 2 0 001.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a href="mailto:support@1to10x.com" className="text-gray-600 hover:text-blue-600 transition-colors duration-200 text-sm">
                  support@1to10x.com
                </a>
              </div>
              
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 text-gray-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <div className="text-gray-600 text-sm">
                  <p>166 Geary St, San Francisco California 94108, United States, <a href="tel:+12128146967" className="hover:text-green-600 transition-colors duration-200">+1 (212) 814-6967</a></p>
                </div>
              </div>
            </div>
          </div>
        </div>



        {/* Bottom Section */}
        <div className="pt-8 border-t border-gray-300">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div className="space-y-3">
              <div className="text-2xl font-bold text-gray-900">
                &copy; 1to10x. All rights reserved.
              </div>
              <span className="inline-flex rounded-md border border-gray-200 bg-white/60 px-4 py-2 text-[11px] sm:text-xs font-medium uppercase tracking-[0.18em] text-gray-600">
                ANM ENABLERS PRIVATE LIMITED
              </span>
            </div>
            <div className="flex flex-col md:flex-row gap-6">
              <Link to="/privacy-policy" className="text-gray-600 hover:text-gray-900 transition-colors duration-200 text-sm">Privacy Policy</Link>
              <Link to="/refund-policy" className="text-gray-600 hover:text-gray-900 transition-colors duration-200 text-sm">Refund Policy</Link>
              <Link to="/terms-of-service" className="text-gray-600 hover:text-gray-900 transition-colors duration-200 text-sm">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
